import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MABiblicalStudies = () => {
  const coreCourses = [
    { code: 'BIB 501', name: 'Introduction to Biblical Studies', credits: 3 },
    { code: 'BIB 502', name: 'Biblical Interpretation and Hermeneutics', credits: 3 },
    { code: 'BIB 503', name: 'Biblical Hebrew I', credits: 3 },
    { code: 'BIB 504', name: 'Biblical Greek I', credits: 3 },
    { code: 'BIB 505', name: 'Old Testament Theology', credits: 3 },
    { code: 'BIB 506', name: 'New Testament Theology', credits: 3 },
    { code: 'BIB 507', name: 'Biblical Research Methods', credits: 3 }
  ];

  const electiveCourses = [
    { code: 'BIB 510', name: 'Advanced Biblical Hebrew', credits: 3 },
    { code: 'BIB 511', name: 'Advanced Biblical Greek', credits: 3 },
    { code: 'BIB 520', name: 'Biblical Archaeology', credits: 3 },
    { code: 'BIB 525', name: 'Dead Sea Scrolls and Biblical Studies', credits: 3 },
    { code: 'BIB 530', name: 'Biblical Geography and History', credits: 3 },
    { code: 'BIB 535', name: 'Ancient Near Eastern Context', credits: 3 },
    { code: 'BIB 540', name: 'Biblical Manuscript Studies', credits: 3 },
    { code: 'BIB 545', name: 'Comparative Ancient Literature', credits: 3 }
  ];

  const thesisCourses = [
    { code: 'BIB 598', name: 'Thesis Research', credits: 3 },
    { code: 'BIB 599', name: 'Thesis Writing and Defense', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Biblical Languages",
      description: "Master Hebrew and Greek for direct engagement with biblical texts",
      icon: "🔤"
    },
    {
      title: "Research Methods",
      description: "Advanced training in biblical research methodology",
      icon: "📚"
    },
    {
      title: "Historical Context",
      description: "Deep understanding of ancient Near Eastern cultures",
      icon: "🏺"
    },
    {
      title: "Theological Integration",
      description: "Connect biblical studies with theological reflection",
      icon: "🤔"
    }
  ];

  const careerPaths = [
    {
      title: "Academic Research",
      description: "Pursue doctoral studies and academic careers",
      icon: "🎓"
    },
    {
      title: "Teaching Ministry",
      description: "Teach in churches, seminaries, or biblical institutes",
      icon: "👨‍🏫"
    },
    {
      title: "Biblical Translation",
      description: "Work in Bible translation and interpretation",
      icon: "📝"
    },
    {
      title: "Ministry Leadership",
      description: "Lead Bible-focused ministries and programs",
      icon: "⛪"
    }
  ];

  const resources = [
    {
      title: "Biblical Research Library",
      description: "Access to extensive biblical and theological resources",
      path: "/academics/ma_biblical_studies/library"
    },
    {
      title: "Language Resources",
      description: "Tools and materials for biblical language study",
      path: "/academics/ma_biblical_studies/languages"
    },
    {
      title: "Research Opportunities",
      description: "Current research projects and collaboration opportunities",
      path: "/academics/ma_biblical_studies/research"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Biblical Studies
            </h1>
            <p className="text-xl opacity-90">
              Advanced scholarship in biblical interpretation and research
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                Our MA in Biblical Studies program provides advanced training in biblical 
                interpretation, languages, and research. Learn more about our{' '}
                <InlineLink to="/academics/ma_biblical_studies/methodology">research methodology</InlineLink> and{' '}
                <InlineLink to="/academics/ma_biblical_studies/approach">academic approach</InlineLink>.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (21 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Elective Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Elective Courses (Select 4 courses - 12 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {electiveCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Thesis Requirements */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Thesis Requirement (6 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {thesisCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Career Paths */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Career Opportunities</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {careerPaths.map((career, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{career.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {career.title}
                      </h3>
                      <p className="text-gray-600">{career.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Academic Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of scholars dedicated to advanced biblical research and interpretation.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MABiblicalStudies; 